import Lottie from 'lottie-react';
import { Mic, MicOff } from 'lucide-react';
import * as React from 'react';

import buddyHoverAnimation from '@/assets/animations/buddyHover.json';
import speakingDotsAnimation from '@/assets/animations/speakingDots.json';
import { cn } from '@/utils';

interface ClosedViewDesktopProps {
  isDragging: boolean;
  wasDragged: boolean;
  handleToggleOpen: () => void;
  message: string;
  setMessage: (message: string) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled: boolean;
  handleVoiceRecord: () => void;
  isListening: boolean;
  speechError: string | null;
  handleSendMessage: () => void;
}

export const ClosedViewDesktop = ({
  isDragging,
  wasDragged,
  handleToggleOpen,
  message,
  setMessage,
  handleKeyPress,
  placeholder,
  disabled,
  handleVoiceRecord,
  isListening,
  speechError,
  handleSendMessage,
}: ClosedViewDesktopProps) => (
  <div
    className={cn(
      'hidden sm:flex',
      'relative',
      'bg-magical-moonlight-d3 border border-magical-moonlight-d1 transition-shadow duration-200',
      'md:rounded-2xl md:h-[76px] md:px-0.5',
      'rounded-xl h-[50px] px-0.5',
      'items-center justify-between',
      'cursor-grab active:cursor-grabbing',
      isDragging && 'shadow-2xl'
    )}
  >
    <button
      type="button"
      className="flex items-center cursor-pointer bg-transparent border-none p-0 pointer-events-auto"
      onClick={e => {
        e.stopPropagation();
        if (!isDragging && !wasDragged) {
          handleToggleOpen();
        }
      }}
      aria-label="Click to open chat"
    >
      <div className="md:w-[68px] md:h-[68px] w-[43.887px] h-[43.887px] flex-shrink-0">
        <Lottie
          animationData={buddyHoverAnimation}
          loop={true}
          autoplay={true}
          className="w-full h-full"
        />
      </div>
      <div className="md:w-8 md:h-8 w-[20.653px] h-[20.653px] flex-shrink-0 md:ml-2 ml-1">
        <Lottie
          animationData={speakingDotsAnimation}
          loop={true}
          autoplay={true}
          className="w-full h-full"
        />
      </div>
    </button>
    <div className="flex-1 md:mx-4 mx-2 pointer-events-auto">
      <div className="relative bg-transparent border border-magical-moonlight-d1 md:rounded-2xl rounded-lg md:h-[60px] h-[39px] flex items-center md:px-4 px-2">
        <input
          type="text"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex-1 bg-transparent border-none outline-none text-white placeholder:text-magical-moonlight-d1 font-medium',
            'md:text-sm text-[9.036px]',
            'focus:ring-0 focus:border-none focus:outline-none'
          )}
        />
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleVoiceRecord();
          }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          disabled={disabled}
          className={cn(
            'flex-shrink-0 md:w-8 md:h-8 w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-200',
            'hover:bg-white/10 focus:bg-white/10 focus:outline-none',
            {
              'bg-bhanzu-orange text-white animate-pulse': isListening,
              'text-magical-moonlight-white hover:text-white': !isListening,
            },
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label={
            isListening ? 'Listening... Click to stop' : 'Start voice recording'
          }
          title={`${
            speechError ?? (isListening ? 'Stop' : 'Start')
          } voice input`}
        >
          {isListening ? (
            <MicOff className="md:w-5 md:h-5 w-3 h-3" />
          ) : (
            <Mic className="md:w-5 md:h-5 w-3 h-3" />
          )}
        </button>
        {message.trim() && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              handleSendMessage();
            }}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            disabled={disabled || !message.trim()}
            className={cn(
              'flex-shrink-0 md:w-10 md:h-10 w-5 h-5 md:rounded-lg rounded-md bg-blue-chaos flex items-center justify-center transition-colors duration-200 md:ml-2 ml-1',
              'hover:bg-blue-chaos/80 focus:outline-none focus:ring-2 focus:ring-blue-chaos/20',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label="Send message"
          >
            <svg
              className="md:w-[26.667px] md:h-[26.667px] w-[13.333px] h-[13.333px]"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                d="M7.4 6.32L15.89 3.49C19.7 2.22 21.77 4.3 20.51 8.11L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.92 14.08L7.4 13.24C1.69 11.34 1.69 8.23 7.4 6.32Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
              <path
                d="M10.11 13.65L13.69 10.06"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  </div>
);
